import { useEffect, useLayoutEffect, useRef } from 'react';

import { expireSessionAndRedirect } from '../../utils/sessionExpiry';
import { decodeSessionTokenCompanyId } from '../../utils/sessionCompanyScope';
import { refreshSession } from '../../utils/sessionRefresh';

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

interface SSEMessage {
  timestamp: number;
  value: string;
  type: string;
  device_id: string;
}

interface UseSSEProps {
  url: string | null;
  /** User JWT for authenticated fetch-streaming. Native EventSource cannot set it. */
  authToken?: string | null;
  onMessage: (data: SSEMessage) => void;
  onError?: (error: Event) => void;
}

interface RenderScope {
  generation: number;
  url: string | null;
  authToken: string | null | undefined;
}

interface EffectCallbacks {
  generation: number;
  onMessage: UseSSEProps['onMessage'];
  onError: UseSSEProps['onError'];
}

export const useSSE = ({ url, authToken, onMessage, onError }: UseSSEProps) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const committedScopeRef = useRef<RenderScope>({
    generation: 0,
    url,
    authToken,
  });
  const committedCallbacksRef = useRef({ onMessage, onError });
  const activeCallbacksRef = useRef<EffectCallbacks | null>(null);

  // Commit the new transport scope synchronously, before passive effect
  // cleanup/setup and before the browser can dispatch another SSE task.
  // Doing this in render would let an abandoned concurrent render invalidate
  // the still-committed stream permanently.
  useLayoutEffect(() => {
    const generation = committedScopeRef.current.generation + 1;
    committedScopeRef.current = {
      generation,
      url,
      authToken,
    };

    // Layout cleanup also runs synchronously on unmount, closing the window
    // before the transport's passive cleanup can abort/close its resources.
    return () => {
      if (committedScopeRef.current.generation === generation) {
        committedScopeRef.current = {
          ...committedScopeRef.current,
          generation: generation + 1,
        };
      }
    };
  }, [url, authToken]);

  // Keep callbacks fresh for the current connection without letting a
  // retired EventSource reach through to a new scope's global "latest" ref.
  // Each transport handler retains its own effect-local callback object.
  useLayoutEffect(() => {
    committedCallbacksRef.current = { onMessage, onError };
    const callbacks = activeCallbacksRef.current;
    if (callbacks?.generation === committedScopeRef.current.generation) {
      callbacks.onMessage = onMessage;
      callbacks.onError = onError;
    }
  }, [onMessage, onError]);

  useEffect(() => {
    const effectGeneration = committedScopeRef.current.generation;
    const effectUrl = url;
    const effectAuthToken = authToken;
    const effectCallbacks: EffectCallbacks = {
      generation: effectGeneration,
      ...committedCallbacksRef.current,
    };
    activeCallbacksRef.current = effectCallbacks;

    let disposed = false;
    let activeController: AbortController | null = null;
    let activeEventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;
    let reconnectAttempt = 0;
    let refreshAttempted = false;

    const isCurrentEffect = () =>
      !disposed &&
      committedScopeRef.current.generation === effectGeneration &&
      committedScopeRef.current.url === effectUrl &&
      committedScopeRef.current.authToken === effectAuthToken;

    const clearReconnectTimer = () => {
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const emitData = (raw: string) => {
      if (!isCurrentEffect()) return;

      try {
        const sanitizedData = raw.replace(/'/g, '"');
        const data: SSEMessage = JSON.parse(sanitizedData);
        if (!isCurrentEffect()) return;
        effectCallbacks.onMessage(data);
      } catch (error) {
        if (!isCurrentEffect()) return;
        console.error('Error parsing SSE data:', error);
      }
    };

    const connectFetchStream = async (requestToken: string) => {
      if (!isCurrentEffect()) return;

      const controller = new AbortController();
      activeController = controller;
      try {
        const response = await fetch(effectUrl as string, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${requestToken}`,
          },
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!isCurrentEffect()) return;

        if (response.status === 401 || response.status === 403) {
          // A single refresh is allowed per render scope. A second rejection
          // of the refreshed credential is terminal instead of refresh-looping.
          if (refreshAttempted) {
            if (!isCurrentEffect()) return;
            expireSessionAndRedirect();
            return;
          }
          refreshAttempted = true;
          const newToken = await refreshSession();
          if (!isCurrentEffect()) return;
          if (newToken) {
            const effectCompanyId =
              decodeSessionTokenCompanyId(effectAuthToken);
            const refreshedCompanyId = decodeSessionTokenCompanyId(newToken);
            // A concurrent impersonation/company switch can make refresh
            // return the replacement tenant's token to this old request.
            // Never pair that credential with the old effect URL.
            if (!effectCompanyId || refreshedCompanyId !== effectCompanyId) {
              return;
            }
            if (!isCurrentEffect()) return;
            scheduleReconnect(newToken);
            return;
          }
          if (!isCurrentEffect()) return;
          expireSessionAndRedirect();
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`SSE request failed (${response.status})`);
        }
        if (!isCurrentEffect()) return;

        reconnectAttempt = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (isCurrentEffect()) {
          const { done, value } = await reader.read();
          if (!isCurrentEffect()) return;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let boundary = buffer.match(/\r?\n\r?\n/);
          while (boundary && boundary.index !== undefined) {
            if (!isCurrentEffect()) return;
            const frame = buffer.slice(0, boundary.index);
            buffer = buffer.slice(boundary.index + boundary[0].length);
            const data = frame
              .split(/\r?\n/)
              .filter((line) => line.startsWith('data:'))
              .map((line) => line.slice(5).trimStart())
              .join('\n');
            if (data) emitData(data);
            if (!isCurrentEffect()) return;
            boundary = buffer.match(/\r?\n\r?\n/);
          }
        }
        if (!isCurrentEffect()) return;
        scheduleReconnect(requestToken);
      } catch (error) {
        if (controller.signal.aborted || !isCurrentEffect()) return;
        console.error('SSE Error:', error);
        if (!isCurrentEffect()) return;
        effectCallbacks.onError?.(new Event('error'));
        if (!isCurrentEffect()) return;
        scheduleReconnect(requestToken);
      } finally {
        if (activeController === controller) {
          activeController = null;
        }
      }
    };

    const connect = (requestToken: string | null | undefined) => {
      if (!isCurrentEffect() || !effectUrl) return;

      if (requestToken) {
        void connectFetchStream(requestToken);
        return;
      }

      const eventSource = new EventSource(effectUrl);
      activeEventSource = eventSource;
      eventSourceRef.current = eventSource;
      const isActiveSource = () =>
        isCurrentEffect() && activeEventSource === eventSource;

      eventSource.onopen = () => {
        if (!isActiveSource()) return;
        reconnectAttempt = 0;
      };

      eventSource.onmessage = (event) => {
        if (!isActiveSource()) return;
        emitData(event.data);
      };

      eventSource.onerror = (error) => {
        if (!isActiveSource()) return;
        console.error('SSE Error:', error);
        if (!isActiveSource()) return;
        effectCallbacks.onError?.(error);
        if (!isActiveSource()) return;
        eventSource.close();
        if (activeEventSource === eventSource) {
          activeEventSource = null;
        }
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }
        scheduleReconnect(requestToken);
      };
    };

    const scheduleReconnect = (requestToken: string | null | undefined) => {
      if (!isCurrentEffect() || reconnectTimer !== null) return;

      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttempt,
        MAX_RECONNECT_DELAY_MS
      );
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        if (!isCurrentEffect()) return;
        connect(requestToken);
      }, delay);
    };

    if (effectUrl) {
      connect(effectAuthToken);
    }

    return () => {
      disposed = true;
      clearReconnectTimer();
      activeController?.abort();
      activeController = null;
      activeEventSource?.close();
      if (eventSourceRef.current === activeEventSource) {
        eventSourceRef.current = null;
      }
      activeEventSource = null;
      if (activeCallbacksRef.current === effectCallbacks) {
        activeCallbacksRef.current = null;
      }
    };
  }, [url, authToken]);

  return eventSourceRef.current;
};
